import { create } from 'zustand';

export type ProcessOverlayKey =
  | 'complianceBorder'
  | 'controlStatus'
  | 'evidenceStatus'
  | 'dependencies'
  | 'tags';

export type ProcessOverlayVisibility = Record<ProcessOverlayKey, boolean>;

export const DEFAULT_PROCESS_OVERLAY_VISIBILITY: ProcessOverlayVisibility = {
  complianceBorder: true,
  controlStatus: true,
  evidenceStatus: true,
  dependencies: true,
  tags: true,
};

interface ProcessViewState {
  collapsedSubprocessIds: string[];
  pendingCenterNodeId: string | null;
  propertyPanelEditMode: boolean;
  overlayVisibility: ProcessOverlayVisibility;

  toggleSubprocessCollapsed: (id: string) => void;
  setCollapsedSubprocessIds: (ids: string[]) => void;
  requestCenterOnNode: (nodeId: string) => void;
  clearCenterOnNode: () => void;
  setPropertyPanelEditMode: (editing: boolean) => void;
  setOverlayVisible: (key: ProcessOverlayKey, visible: boolean) => void;
  toggleOverlay: (key: ProcessOverlayKey) => void;
  resetOverlayVisibility: () => void;
}

export const useProcessViewStore = create<ProcessViewState>()((set) => ({
  collapsedSubprocessIds: [],
  pendingCenterNodeId: null,
  propertyPanelEditMode: false,
  overlayVisibility: DEFAULT_PROCESS_OVERLAY_VISIBILITY,

  toggleSubprocessCollapsed: (id) =>
    set((state) => ({
      collapsedSubprocessIds: state.collapsedSubprocessIds.includes(id)
        ? state.collapsedSubprocessIds.filter((s) => s !== id)
        : [...state.collapsedSubprocessIds, id],
    })),
  setCollapsedSubprocessIds: (ids) => set({ collapsedSubprocessIds: ids }),
  requestCenterOnNode: (nodeId) => set({ pendingCenterNodeId: nodeId }),
  clearCenterOnNode: () => set({ pendingCenterNodeId: null }),
  setPropertyPanelEditMode: (editing) => set({ propertyPanelEditMode: editing }),
  setOverlayVisible: (key, visible) =>
    set((state) =>
      state.overlayVisibility[key] === visible
        ? state
        : {
            overlayVisibility: {
              ...state.overlayVisibility,
              [key]: visible,
            },
          },
    ),
  toggleOverlay: (key) =>
    set((state) => ({
      overlayVisibility: {
        ...state.overlayVisibility,
        [key]: !state.overlayVisibility[key],
      },
    })),
  resetOverlayVisibility: () => set({ overlayVisibility: DEFAULT_PROCESS_OVERLAY_VISIBILITY }),
}));
